/**
 * Floating Tour Chat Component
 * Follows user across all pages during tour mode
 * Allows users to ask questions while exploring
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { isTourActive, setTourActive, setCurrentTourSection, getCurrentTourSection, getWelcomeMessage, getSectionWelcomeMessage, SECTION_QA, TOUR_SECTIONS, clearTourMessages, saveTourMessages } from '../../utils/interactiveTour';
import { openChat } from '../../store/chat/chatSlice';
import { TourOptions } from './TourOptions';
import { TourPresetQuestions } from './TourPresetQuestions';
import { processQuestionForDizee } from '../../pages/Manager/api/openaiService';
import ConfirmModal from '../Utils/ConfirmModal';
import { useConfirm } from '../../hooks/useConfirm';

const ChatContainer = styled(motion.div)`
    position: fixed;
    bottom: ${props => props.isExpanded ? '0' : '20px'};
    left: 0;
    right: 0;
    width: 100%;
    max-width: 390px;
    height: ${props => props.isExpanded ? '100vh' : 'auto'};
    max-height: ${props => props.isExpanded ? '100vh' : 'auto'};
    background: ${props => props.isExpanded ? '#000000' : 'transparent'};
    border: ${props => props.isExpanded ? 'none' : 'none'};
    border-radius: ${props => props.isExpanded ? '0' : '0'};
    box-shadow: none;
    display: flex;
    flex-direction: column;
    z-index: 9999;
    overflow: ${props => props.isExpanded ? 'hidden' : 'visible'};
    margin: 0 auto;
    
    @media (max-width: 768px) {
        /* Mobile: full width */
        max-width: 100%;
        bottom: ${props => props.isExpanded ? '0' : '10px'};
    }
`;

const ChatHeader = styled.div`
    padding: 16px;
    background: linear-gradient(135deg, #FF2300 0%, #FF4520 100%);
    display: ${props => props.isExpanded ? 'flex' : 'none'};
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const ChatTitle = styled.div`
    color: #FFFFFF;
    font-size: 16px;
    font-weight: 600;
`;

const HeaderButton = styled.button`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 8px 16px;
    background: rgba(0, 0, 0, 0.5);
    color: rgba(255, 255, 255, 0.7);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 20px;
    cursor: pointer;
    font-size: 13px;
    font-weight: 400;
    backdrop-filter: blur(10px);
    transition: all 0.3s ease;
    
    &:hover {
        color: rgba(255, 255, 255, 1);
        background: rgba(0, 0, 0, 0.7);
        border-color: rgba(255, 255, 255, 0.2);
    }
`;

const ExitButton = styled.button`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 8px 16px;
    background: rgba(255, 35, 0, 0.1);
    color: #FF2300;
    border: none;
    border-radius: 20px;
    cursor: pointer;
    font-size: 13px;
    font-weight: 400;
    transition: all 0.3s ease;
    
    &:hover {
        background: rgba(255, 35, 0, 0.2);
    }
`;

const MessagesArea = styled.div`
    flex: 1;
    overflow-y: auto;
    padding: 16px 16px 16px 0;
    display: ${props => props.isExpanded ? 'flex' : 'none'};
    flex-direction: column;
    gap: 12px;
    scrollbar-width: none;
    
    &::-webkit-scrollbar {
        display: none;
    }
`;

const Message = styled(motion.div)`
    padding: 12px 16px;
    border-radius: 12px;
    font-size: 14px;
    line-height: 1.6;
    background: ${props => props.isUser ? 'rgba(255, 255, 255, 0.1)' : 'transparent'};
    color: #FFFFFF;
    align-self: ${props => props.isUser ? 'flex-end' : 'flex-start'};
    margin-left: ${props => props.isUser ? 'auto' : '16px'};
    margin-right: ${props => props.isUser ? '16px' : 'auto'};
    max-width: 85%;
    word-wrap: break-word;
`;

const ThinkingIndicator = styled.div`
    color: #8E8E93;
    font-size: 14px;
    padding: 12px 16px;
    margin-left: 16px;
    display: flex;
    align-items: center;
    
    &::after {
        content: '';
        animation: thinking 1.2s infinite;
        margin-left: 2px;
    }
    
    @keyframes thinking {
        0% { content: ''; }
        33% { content: '.'; }
        66% { content: '..'; }
        100% { content: '...'; }
    }
`;

const NavigationMessage = styled(motion.div)`
    padding: 10px 16px;
    border-radius: 12px;
    font-size: 12px;
    line-height: 1.6;
    background: transparent;
    color: #8E8E93;
    align-self: flex-start;
    margin-left: 16px;
    max-width: 85%;
    word-wrap: break-word;
`;

const AskAIButton = styled.button`
    padding: 8px 16px;
    margin-top: 12px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    border: 1px solid rgba(255, 35, 0, 0.3);
    background: rgba(255, 35, 0, 0.05);
    color: #FF2300;
    text-align: center;
    
    &:hover {
        background: rgba(255, 35, 0, 0.1);
        border-color: rgba(255, 35, 0, 0.4);
        transform: translateY(-1px);
    }
    
    &:active {
        transform: scale(0.98);
    }
    
    @media (max-width: 480px) {
        padding: 7px 14px;
        font-size: 12px;
    }
`;

const InputArea = styled.div`
    padding: ${props => props.isExpanded ? '8px 16px' : '16px'};
    border-top: none;
    background: ${props => props.isExpanded ? 'transparent' : 'rgba(0, 0, 0, 0.95)'};
    border-radius: ${props => props.isExpanded ? '0' : '16px'};
    backdrop-filter: ${props => props.isExpanded ? 'none' : 'blur(10px)'};
    box-shadow: ${props => props.isExpanded ? 'none' : '0 4px 20px rgba(0, 0, 0, 0.5)'};
`;

const ControlButtons = styled.div`
    display: flex;
    justify-content: center;
    gap: 12px;
    margin-top: 12px;
`;

const InputRow = styled.div`
    flex: 1;
    display: flex;
    align-items: center;
    position: relative;
    width: 100%;
`;

const Input = styled.input`
    flex: 1;
    background: rgba(0, 0, 0, 0.4);
    border: 2px solid #FF2300;
    border-radius: 50px;
    padding: 12px 52px 12px 20px;
    color: rgba(255, 255, 255, 0.9);
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
    font-size: 15px;
    font-weight: 400;
    outline: none;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 0 20px rgba(255, 35, 0, 0.3), 0 0 40px rgba(255, 35, 0, 0.15);
    width: 100%;
    
    &::placeholder {
        color: rgba(255, 255, 255, 0.4);
        transition: color 0.3s ease;
    }

    &:focus {
        border-color: #FF2300;
        box-shadow: 0 0 30px rgba(255, 35, 0, 0.4), 0 0 60px rgba(255, 35, 0, 0.2);
    }
    
    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const SendButton = styled.button`
    position: absolute;
    right: 4px;
    background: ${props => props.hasText ? '#FF2300' : 'rgba(255, 255, 255, 0.1)'};
    border: none;
    border-radius: 50%;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${props => props.hasText ? '#000000' : 'rgba(255, 255, 255, 0.3)'};
    font-size: 18px;
    cursor: ${props => props.hasText ? 'pointer' : 'not-allowed'};
    transition: all 0.3s ease;
    
    &:hover {
        ${props => props.hasText && `
            background: #ff3a1a;
            transform: scale(1.05);
        `}
    }
    
    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const LearnButton = styled(motion.button)`
    width: 100%;
    padding: 16px 24px;
    margin-bottom: 12px;
    background: linear-gradient(135deg, #FF2300 0%, #FF4520 100%);
    color: #FFFFFF;
    border: none;
    border-radius: 12px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 4px 20px rgba(255, 35, 0, 0.4);
    
    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 25px rgba(255, 35, 0, 0.5);
    }
    
    &:active {
        transform: translateY(0);
    }
`;


// Format message text with markdown support
const formatMessageText = (text) => {
    if (!text || typeof text !== 'string') return null;
    
    return text.split('\n').map((line, index) => {
        // Handle bullet points
        if (line.trim().startsWith('•')) {
            const content = line.trim().substring(1).trim();
            return (
                <div key={index} style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                    <span style={{ color: '#FF2300', fontWeight: 'bold' }}>•</span>
                    <span dangerouslySetInnerHTML={{ 
                        __html: content.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
                    }} />
                </div>
            );
        }
        
        // Handle empty lines
        if (line.trim() === '') {
            return <br key={index} />;
        }
        
        // Handle bold text (**text**)
        const formatted = line.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
        return <div key={index} dangerouslySetInnerHTML={{ __html: formatted }} />;
    });
};

export const FloatingTourChat = ({ 
    messages, 
    setMessages, 
    onTourOptionSelect, 
    onTourQuestionSelect 
}) => {
    const [showChat, setShowChat] = useState(false);
    const [userInput, setUserInput] = useState('');
    const [isExpanded, setIsExpanded] = useState(false); // Start in simplify mode
    const [isLoading, setIsLoading] = useState(false);
    const [showLearnButton, setShowLearnButton] = useState(true); // Show orange button by default
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    const messagesEndRef = useRef(null);
    const isHome = location.pathname === '/';
    const { confirmState, showConfirm, handleClose, handleConfirm } = useConfirm();
    
    // Helper function to get section from current route
    const getSectionFromRoute = (pathname) => {
        if (pathname.startsWith('/bookings')) return 'bookings';
        if (pathname.startsWith('/catalog')) return 'catalog';
        if (pathname.startsWith('/knowledge')) return 'knowledge';
        if (pathname.startsWith('/flows')) return 'flows';
        if (pathname.startsWith('/tour-manager')) return 'tourmanager';
        if (pathname.startsWith('/notes')) return 'notes';
        if (pathname.startsWith('/connectors')) return 'connectors';
        if (pathname.startsWith('/analytics')) return 'analytics';
        if (pathname.startsWith('/contacts')) return 'contacts';
        return null;
    };
    
    // Check tour status and sync current section with route
    useEffect(() => {
        const checkTourStatus = () => {
            setShowChat(isTourActive());
            
            // Sync current section with route if in tour mode
            if (isTourActive()) {
                const routeSection = getSectionFromRoute(location.pathname);
                const currentSection = getCurrentTourSection();
                
                // If we're on a section page and the current section doesn't match, update it
                if (routeSection && routeSection !== currentSection) {
                    console.log(`🔄 Syncing tour section: ${currentSection} → ${routeSection}`);
                    setCurrentTourSection(routeSection);
                }
            }
        };
        
        checkTourStatus();
        const interval = setInterval(checkTourStatus, 500);
        
        return () => clearInterval(interval);
    }, [location.pathname]);
    
    // Always collapse to simplified mode when navigating to a section page and show learn button
    useEffect(() => {
        const routeSection = getSectionFromRoute(location.pathname);
        if (routeSection && !isHome) {
            console.log('📍 Arrived at section page, collapsing to simplified mode');
            setIsExpanded(false);
            setShowLearnButton(true); // Show the orange button when arriving at a new section
        }
    }, [location.pathname, isHome]);
    
    // Auto-scroll to bottom
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);
    
    const handleExitTour = async () => {
        console.log('🚪 Exit Tour button clicked');
        try {
            const confirmed = await showConfirm({
                title: 'Exit Tour',
                message: 'Are you sure you want to exit the tour?',
                confirmLabel: 'Exit Tour',
                cancelLabel: 'Cancel'
            });
            
            console.log('🚪 Exit Tour confirmation result:', confirmed);
            
            if (confirmed) {
                console.log('🚪 Exiting tour...');
                setTourActive(false);
                setCurrentTourSection(null);
                setMessages([]);
                clearTourMessages(); // Clear saved messages
                
                // Navigate to home to exit tour completely
                navigate('/');
                console.log('🚪 Tour exited successfully');
            }
        } catch (error) {
            console.error('🚪 Error in handleExitTour:', error);
        }
    };
    
    const handleBackToChat = () => {
        // Navigate back to homepage and restart tour (like "Start your setup tour")
        if (!isHome) {
            // Clear current tour messages in global state
            const welcome = getWelcomeMessage();
            setMessages([welcome]);
            setCurrentTourSection(null);
            
            // Signal to homepage to restart the tour
            sessionStorage.setItem('restartTourOnLoad', 'true');
            clearTourMessages(); // Clear saved messages for fresh restart
            dispatch(openChat());
            navigate('/');
        }
    };
    
    const handleToggleExpand = () => {
        const willExpand = !isExpanded;
        setIsExpanded(willExpand);
        
        // If expanding and we're in a section without a welcome message yet, add it
        if (willExpand) {
            setShowLearnButton(false); // Hide the orange button once expanded
            
            const currentSection = getCurrentTourSection();
            if (currentSection) {
                // Check if we need to show the welcome message for THIS SPECIFIC section
                const hasWelcomeForThisSection = messages.some(m => 
                    m.showCurrentSection === currentSection
                );
                
                // Only show welcome if we haven't shown one for this section yet
                if (!hasWelcomeForThisSection) {
                    const welcomeMessage = getSectionWelcomeMessage(currentSection);
                    if (welcomeMessage) {
                        // Append section welcome to existing conversation (keep navigation message)
                        setMessages(prev => [...prev, welcomeMessage]);
                        setMessages(current => {
                            saveTourMessages(current);
                            return current;
                        });
                    }
                }
            }
        }
    };
    
    const handleAskAI = async (question) => {
        if (isLoading) return;
        
        setIsLoading(true);
        const currentSection = getCurrentTourSection();
        
        try {
            // Build context-aware system prompt
            const sectionInfo = currentSection ? TOUR_SECTIONS[currentSection] : null;
            let contextPrompt = `You are a helpful tour guide for DIZEE, a platform for artists to manage their music career. You're currently in TOUR MODE, helping a user explore the platform.`;
            
            if (sectionInfo) {
                contextPrompt += `\n\nThe user is currently viewing the **${sectionInfo.name}** section of DIZEE.`;
                
                // Add section-specific context
                const sectionDescriptions = {
                    bookings: 'This is where users manage all their shows, gigs, and events. They can add shows, track payments, manage venue details, and see their tour calendar.',
                    catalog: 'This is the music library where users manage all their tracks, EPs, and albums. They can add released and unreleased music, upload audio files, add artwork, and track streaming stats.',
                    knowledge: 'This is the digital filing cabinet for contracts, contacts, press photos, EPKs, tech riders, and important documents. Everything is organized with tags and searchable.',
                    reminders: 'This is where users set reminders for important dates and tasks. They can link reminders to shows, tracks, or documents, and get notifications.'
                };
                
                if (sectionDescriptions[currentSection]) {
                    contextPrompt += `\n\n${sectionDescriptions[currentSection]}`;
                }
                
                contextPrompt += `\n\nProvide helpful, concise answers about how to use this section. Since the user is already IN this section, don't suggest they navigate to it. Be friendly and encouraging. Keep responses short and actionable.`;
            } else {
                contextPrompt += `\n\nProvide helpful guidance about DIZEE's features. Be friendly and encouraging.`;
            }
            
            // Prepare messages for AI with system context
            const conversationHistory = [
                {
                    role: 'system',
                    content: contextPrompt
                },
                ...messages
                    .filter(m => m.role === 'user' || m.role === 'assistant')
                    .map(m => ({
                        role: m.role,
                        content: m.text
                    })),
                {
                    role: 'user',
                    content: question
                }
            ];
            
            // Call AI with context - parameters: question, conversationHistory, dispatch, conversationId, images
            const response = await processQuestionForDizee(
                question,
                conversationHistory,
                null, // no dispatch
                null, // no conversationId - tour mode doesn't track conversations
                []    // no images
            );
            
            // Ensure response is a string - processQuestionForDizee returns {success, response, conversation}
            const responseText = typeof response === 'string' ? response : response?.response || 'I can help you with that! Try asking one of the questions below.';
            
            // Replace the last message with AI response
            setMessages(prev => {
                const updated = [...prev];
                updated[updated.length - 1] = {
                    role: 'assistant',
                    text: responseText,
                    isTourMessage: true,
                    showQuestions: true
                };
                return updated;
            });
            
        } catch (error) {
            console.error('Error getting AI response:', error);
            
            // Show error message
            setMessages(prev => {
                const updated = [...prev];
                updated[updated.length - 1] = {
                    role: 'assistant',
                    text: 'Sorry, I had trouble getting a response. Try asking one of the questions below.',
                    isTourMessage: true,
                    showQuestions: true
                };
                return updated;
            });
        } finally {
            setIsLoading(false);
        }
    };
    
    const handleQuestionClick = async (question) => {
        if (isLoading) return;
        
        // Auto-expand chat and hide learn button
        setIsExpanded(true);
        setShowLearnButton(false);
        
        const currentSection = getCurrentTourSection();
        
        // Check if we need to show the welcome message for THIS SPECIFIC section
        const hasWelcomeForThisSection = currentSection && messages.some(m => 
            m.showCurrentSection === currentSection
        );
        
        if (currentSection && !hasWelcomeForThisSection) {
            const welcomeMessage = getSectionWelcomeMessage(currentSection);
            if (welcomeMessage) {
                // Append welcome message and user question to existing conversation
                const userQuestion = {
                    role: 'user',
                    text: question,
                    isTourMessage: true
                };
                setMessages(prev => [...prev, welcomeMessage, userQuestion]);
                setMessages(current => {
                    saveTourMessages(current);
                    return current;
                });
            }
        } else {
            // Add user question to existing messages
            setMessages(prev => [...prev, {
                role: 'user',
                text: question,
                isTourMessage: true
            }]);
        }
        
        // Add thinking indicator
        const thinkingMessage = {
            role: 'assistant',
            text: 'Thinking',
            isThinking: true
        };
        setMessages(prev => [...prev, thinkingMessage]);
        
        setIsLoading(true);
        
        try {
            // Build context-aware system prompt (same as handleSend)
            const sectionInfo = currentSection ? TOUR_SECTIONS[currentSection] : null;
            let contextPrompt = `You are a helpful tour guide for DIZEE, a platform for artists to manage their music career. You're currently in TOUR MODE, helping a user explore the platform.`;
            
            if (sectionInfo) {
                contextPrompt += `\n\nThe user is currently viewing the **${sectionInfo.name}** section of DIZEE.`;
                
                // Add section-specific context
                const sectionDescriptions = {
                    bookings: 'This is where users manage all their shows, gigs, and events. They can add shows, track payments, manage venue details, and see their tour calendar.',
                    catalog: 'This is the music library where users manage all their tracks, EPs, and albums. They can add released and unreleased music, upload audio files, add artwork, and track streaming stats.',
                    knowledge: 'This is the digital filing cabinet for contracts, contacts, press photos, EPKs, tech riders, and important documents. Everything is organized with tags and searchable.',
                    reminders: 'This is where users set reminders for important dates and tasks. They can link reminders to shows, tracks, or documents, and get notifications.'
                };
                
                if (sectionDescriptions[currentSection]) {
                    contextPrompt += `\n\n${sectionDescriptions[currentSection]}`;
                }
                
                contextPrompt += `\n\nProvide helpful, concise answers about how to use this section. Since the user is already IN this section, don't suggest they navigate to it. Be friendly and encouraging. Keep responses short and actionable.`;
            } else {
                contextPrompt += `\n\nProvide helpful guidance about DIZEE's features. Be friendly and encouraging.`;
            }
            
            // Prepare messages for AI with system context
            const conversationHistory = [
                {
                    role: 'system',
                    content: contextPrompt
                },
                ...messages
                    .filter(m => m.role === 'user' || m.role === 'assistant')
                    .map(m => ({
                        role: m.role,
                        content: m.text
                    })),
                {
                    role: 'user',
                    content: question
                }
            ];
            
            // Call AI with context - parameters: question, conversationHistory, dispatch, conversationId, images
            const response = await processQuestionForDizee(
                question,
                conversationHistory,
                null, // no dispatch
                null, // no conversationId - tour mode doesn't track conversations
                []    // no images
            );
            
            // Ensure response is a string - processQuestionForDizee returns {success, response, conversation}
            const responseText = typeof response === 'string' ? response : response?.response || 'I can help you with that! Try asking one of the questions below.';
            
            // Remove thinking indicator and add AI response
            setMessages(prev => {
                const updated = [...prev];
                const lastIndex = updated.length - 1;
                if (updated[lastIndex] && updated[lastIndex].isThinking) {
                    updated.splice(lastIndex, 1); // Remove thinking message
                }
                return [...updated, {
                    role: 'assistant',
                    text: responseText,
                    isTourMessage: true,
                    showQuestions: true
                }];
            });
            
        } catch (error) {
            console.error('Error getting AI response for preset question:', error);
            
            // Fallback to helpful response with Ask AI option
            const sectionInfo = currentSection ? TOUR_SECTIONS[currentSection] : null;
            let fallbackText = `I'm here to help you with ${sectionInfo ? `**${sectionInfo.name}**` : 'DIZEE'}! `;
            fallbackText += `Try asking one of the questions below, or feel free to explore the features on this page.`;
            
            // Remove thinking indicator and add fallback response
            setMessages(prev => {
                const updated = [...prev];
                const lastIndex = updated.length - 1;
                if (updated[lastIndex] && updated[lastIndex].isThinking) {
                    updated.splice(lastIndex, 1); // Remove thinking message
                }
                return [...updated, {
                    role: 'assistant',
                    text: fallbackText,
                    isTourMessage: true,
                    showQuestions: true,
                    showAskAI: true,
                    originalQuestion: question
                }];
            });
        } finally {
            setIsLoading(false);
        }
    };
    
    const handleSend = async () => {
        if (!userInput.trim() || isLoading) return;
        
        // Auto-expand chat when sending a message and hide learn button
        setIsExpanded(true);
        setShowLearnButton(false);
        
        const question = userInput.trim();
        const currentSection = getCurrentTourSection();
        
        // Check if we need to show the welcome message for THIS SPECIFIC section
        const hasWelcomeForThisSection = currentSection && messages.some(m => 
            m.showCurrentSection === currentSection
        );
        
        if (currentSection && !hasWelcomeForThisSection) {
            const welcomeMessage = getSectionWelcomeMessage(currentSection);
            if (welcomeMessage) {
                // Append welcome message and user message to existing conversation
                const userMessage = {
                    role: 'user',
                    text: question,
                    isTourMessage: true
                };
                setMessages(prev => [...prev, welcomeMessage, userMessage]);
                setMessages(current => {
                    saveTourMessages(current);
                    return current;
                });
            }
        } else {
            // Add user message to existing messages
            setMessages(prev => [...prev, {
                role: 'user',
                text: question,
                isTourMessage: true
            }]);
        }
        
        setUserInput('');
        
        // Add thinking indicator
        const thinkingMessage = {
            role: 'assistant',
            text: 'Thinking',
            isThinking: true
        };
        setMessages(prev => [...prev, thinkingMessage]);
        
        setIsLoading(true);
        
        try {
            // Build context-aware system prompt
            const sectionInfo = currentSection ? TOUR_SECTIONS[currentSection] : null;
            let contextPrompt = `You are a helpful tour guide for DIZEE, a platform for artists to manage their music career. You're currently in TOUR MODE, helping a user explore the platform.`;
            
            if (sectionInfo) {
                contextPrompt += `\n\nThe user is currently viewing the **${sectionInfo.name}** section of DIZEE.`;
                
                // Add section-specific context
                const sectionDescriptions = {
                    bookings: 'This is where users manage all their shows, gigs, and events. They can add shows, track payments, manage venue details, and see their tour calendar.',
                    catalog: 'This is the music library where users manage all their tracks, EPs, and albums. They can add released and unreleased music, upload audio files, add artwork, and track streaming stats.',
                    knowledge: 'This is the digital filing cabinet for contracts, contacts, press photos, EPKs, tech riders, and important documents. Everything is organized with tags and searchable.',
                    reminders: 'This is where users set reminders for important dates and tasks. They can link reminders to shows, tracks, or documents, and get notifications.'
                };
                
                if (sectionDescriptions[currentSection]) {
                    contextPrompt += `\n\n${sectionDescriptions[currentSection]}`;
                }
                
                contextPrompt += `\n\nProvide helpful, concise answers about how to use this section. Since the user is already IN this section, don't suggest they navigate to it. Be friendly and encouraging. Keep responses short and actionable.`;
            } else {
                contextPrompt += `\n\nProvide helpful guidance about DIZEE's features. Be friendly and encouraging.`;
            }
            
            // Prepare messages for AI with system context
            const conversationHistory = [
                {
                    role: 'system',
                    content: contextPrompt
                },
                ...messages
                    .filter(m => m.role === 'user' || m.role === 'assistant')
                    .map(m => ({
                        role: m.role,
                        content: m.text
                    })),
                {
                    role: 'user',
                    content: question
                }
            ];
            
            // Call AI with context - parameters: question, conversationHistory, dispatch, conversationId, images
            const response = await processQuestionForDizee(
                question,
                conversationHistory,
                null, // no dispatch
                null, // no conversationId - tour mode doesn't track conversations
                []    // no images
            );
            
            // Ensure response is a string - processQuestionForDizee returns {success, response, conversation}
            const responseText = typeof response === 'string' ? response : response?.response || 'I can help you with that!';
            
            // Remove thinking indicator and add AI response - for custom messages, show "Reload menu" button instead of preset questions
            setMessages(prev => {
                const updated = [...prev];
                const lastIndex = updated.length - 1;
                if (updated[lastIndex] && updated[lastIndex].isThinking) {
                    updated.splice(lastIndex, 1); // Remove thinking message
                }
                return [...updated, {
                    role: 'assistant',
                    text: responseText,
                    isTourMessage: true,
                    showQuestions: false, // Don't show preset questions for custom messages
                    showOptions: true, // Show menu option
                    options: ['menu']
                }];
            });
            
        } catch (error) {
            console.error('Error getting AI response:', error);
            
            // Fallback to helpful response with Ask AI option
            const sectionInfo = currentSection ? TOUR_SECTIONS[currentSection] : null;
            let fallbackText = `I'm here to help you with ${sectionInfo ? `**${sectionInfo.name}**` : 'DIZEE'}! `;
            fallbackText += `Try asking one of the questions below, or feel free to explore the features on this page.`;
            
            // Remove thinking indicator and add fallback response
            setMessages(prev => {
                const updated = [...prev];
                const lastIndex = updated.length - 1;
                if (updated[lastIndex] && updated[lastIndex].isThinking) {
                    updated.splice(lastIndex, 1); // Remove thinking message
                }
                return [...updated, {
                    role: 'assistant',
                    text: fallbackText,
                    isTourMessage: true,
                    showQuestions: false,
                    showOptions: true,
                    options: ['menu'],
                    showAskAI: true,
                    originalQuestion: question
                }];
            });
        } finally {
            setIsLoading(false);
        }
    };
    
    if (!showChat) return null;
    
    // Don't show on homepage when chat tab is active - FooterBar handles it there
    if (isHome) return null;
    
    return (
        <>
            <ConfirmModal
                isOpen={confirmState.isOpen}
                onClose={handleClose}
                onConfirm={handleConfirm}
                title={confirmState.title}
                message={confirmState.message}
                confirmLabel={confirmState.confirmLabel}
                cancelLabel={confirmState.cancelLabel}
                confirmStyle={confirmState.confirmStyle}
            />
            <ChatContainer
                isExpanded={isExpanded}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
            >
            <MessagesArea isExpanded={isExpanded}>
                {messages.map((msg, idx) => (
                    msg.isThinking ? (
                        <ThinkingIndicator key={idx}>
                            {msg.text}
                        </ThinkingIndicator>
                    ) : msg.isNavigationMessage ? (
                        <NavigationMessage key={idx}>
                            {formatMessageText(msg.text)}
                        </NavigationMessage>
                    ) : (
                        <Message key={idx} isUser={msg.role === 'user'}>
                            {/* Render formatted text */}
                            {msg.text && !msg.showCurrentSection && formatMessageText(msg.text)}
                            
                            {/* Render text with colored section button */}
                            {msg.text && msg.showCurrentSection && (
                                <div>
                                    {msg.text}
                                    <span
                                        style={{
                                            color: TOUR_SECTIONS[msg.showCurrentSection].color,
                                            fontWeight: 600,
                                            cursor: 'default'
                                        }}
                                    >
                                        {TOUR_SECTIONS[msg.showCurrentSection].name}
                                    </span>
                                    {msg.continuationText && formatMessageText(msg.continuationText)}
                                </div>
                            )}
                            
                            {/* Render tour options inline */}
                            {msg.isTourMessage && msg.showOptions && msg.options && (
                                <div style={{ marginTop: '16px' }}>
                                    <div style={{ color: 'green', fontSize: '12px', marginBottom: '8px' }}>
                                        DEBUG: Showing {msg.options.length} options: {msg.options.join(', ')}
                                    </div>
                                    <TourOptions 
                                        options={msg.options}
                                        onOptionSelect={onTourOptionSelect}
                                    />
                                </div>
                            )}
                        
                        {/* Render tour questions inline */}
                        {msg.isTourMessage && msg.showQuestions && (
                            <div style={{ marginTop: '12px' }}>
                                <div style={{ color: 'red', fontSize: '12px', marginBottom: '8px' }}>
                                    DEBUG: Showing questions for section: {getCurrentTourSection() || 'none'}
                                </div>
                                <TourPresetQuestions 
                                    onQuestionSelect={onTourQuestionSelect || handleQuestionClick}
                                    currentSection={getCurrentTourSection()}
                                />
                            </div>
                        )}
                        
                            {/* Render Ask AI button for generic responses */}
                            {msg.isTourMessage && msg.showAskAI && msg.originalQuestion && (
                                <AskAIButton onClick={() => handleAskAI(msg.originalQuestion)}>
                                    Get another answer to your question with AI
                                </AskAIButton>
                            )}
                        </Message>
                    )
                ))}
                <div ref={messagesEndRef} />
            </MessagesArea>
            
            <InputArea isExpanded={isExpanded}>
                {/* Show prominent orange button when collapsed and on a section page */}
                {!isExpanded && showLearnButton && getCurrentTourSection() && (
                    <AnimatePresence>
                        <LearnButton
                            onClick={handleToggleExpand}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 10 }}
                        >
                            Learn about this app
                        </LearnButton>
                    </AnimatePresence>
                )}
                
                {/* Only show input row and controls when learn button is not visible */}
                {!(showLearnButton && !isExpanded) && (
                    <>
                        <InputRow>
                            <Input
                                value={userInput}
                                onChange={(e) => setUserInput(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && !isLoading && handleSend()}
                                placeholder="Ask me anything..."
                                disabled={isLoading}
                            />
                            <SendButton onClick={handleSend} hasText={!!userInput.trim()} disabled={!userInput.trim() || isLoading}>
                                ↗
                            </SendButton>
                        </InputRow>
                        
                        <ControlButtons>
                            <HeaderButton onClick={handleToggleExpand}>
                                {isExpanded ? 'Simplify' : 'Expand'}
                            </HeaderButton>
                            {!isHome && (
                                <HeaderButton onClick={handleBackToChat}>
                                    Back to Chat
                                </HeaderButton>
                            )}
                            <ExitButton onClick={handleExitTour}>
                                Exit Tour
                            </ExitButton>
                        </ControlButtons>
                    </>
                )}
            </InputArea>
        </ChatContainer>
        </>
    );
};

export default FloatingTourChat;

